import { Alert, AlertIcon, Box, Button, CloseButton, Container, Link, Text } from '@chakra-ui/react'
import React from 'react'
import { useHistory, useLocation } from 'react-router-dom'

/**
 * Campaign banner for the 1 September 2026 move to automated billing.
 *
 * Shown on every authenticated page until the cutoff. It is dismissible
 * everywhere EXCEPT the analytics and settings pages — the two places where the
 * notice is the point of the page, so it stays put there.
 */

/**
 * When the change takes effect, and therefore when this banner stops showing.
 *
 * MUST stay in step with the backend cutoff
 * (`DEFAULT_MANUAL_ANALYTICS_CUTOFF` in baotree-backend/utils/featureEntitlements.js,
 * overridable there via `MANUAL_PLAN_ANALYTICS_CUTOFF`). The explicit +02:00
 * offset is deliberate: without it the deadline drifts with the viewer's
 * timezone, so someone in UTC would see the banner disappear two hours early.
 */
export const BILLING_TRANSITION_CUTOFF = new Date('2026-09-01T00:00:00+02:00')

/** Where "Choose your plan" goes — the Subscription tab in Settings. */
const CHOOSE_PLAN_PATH = '/auth/settings?tab=3'

const SUPPORT_EMAIL = 'customers@baotree.io'

/**
 * Routes where the banner may NOT be dismissed.
 *
 * Matched by prefix so nested screens are covered too — a customer reading a
 * single report is still "on analytics" and should still see the notice.
 */
const NON_DISMISSIBLE_PATH_PREFIXES = ['/auth/insight-analytics', '/auth/settings']

/** localStorage key holding the dismissal flag. */
const DISMISSED_STORAGE_KEY = 'baotree.billingTransitionBanner.dismissed.v1'

const isNonDismissibleRoute = (pathname: string): boolean =>
  NON_DISMISSIBLE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )

/** Read the persisted dismissal, tolerating storage being unavailable. */
const readDismissed = (): boolean => {
  try {
    return window.localStorage.getItem(DISMISSED_STORAGE_KEY) === 'true'
  } catch {
    // Private mode / blocked storage: treat as not dismissed. Showing the notice
    // again is the safe failure for a billing deadline.
    return false
  }
}

const persistDismissed = (): void => {
  try {
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, 'true')
  } catch {
    // Best-effort only — dismissal simply will not survive a reload.
  }
}

type BillingTransitionBannerProps = {
  /** Injectable for tests/storybook; defaults to now. */
  now?: Date
}

const BillingTransitionBanner: React.FC<BillingTransitionBannerProps> = ({ now = new Date() }) => {
  const history = useHistory()
  const { pathname } = useLocation()

  const [dismissed, setDismissed] = React.useState<boolean>(readDismissed)

  // The notice is about a change that takes effect on the cutoff; afterwards it
  // is stale, so it retires itself rather than needing a follow-up deploy.
  if (now.getTime() >= BILLING_TRANSITION_CUTOFF.getTime()) return null

  const canDismiss = !isNonDismissibleRoute(pathname)
  if (canDismiss && dismissed) return null

  const handleDismiss = () => {
    setDismissed(true)
    persistDismissed()
  }

  return (
    <Container position="relative" maxW="8xl" mt={0} mb={6} px={{ base: 4, md: 14 }}>
      <Alert
        status="warning"
        alignItems="flex-start"
        borderRadius="0"
        py={4}
        pr={canDismiss ? 10 : 4}
      >
        <AlertIcon mt={1} />

        <Box flex="1">
          <Text fontWeight="600" fontSize="sm" color="gray.800" mb={2}>
            Changes to Billing and Analytics Access
          </Text>

          <Text fontSize="sm" color="gray.700" lineHeight="1.7" mb={2}>
            From 1 September 2026, Baotree is moving to automated billing, making it easier to
            manage your subscription. To ensure uninterrupted access, please select your plan and
            add your billing details before this date.
          </Text>

          <Text fontSize="sm" color="gray.700" lineHeight="1.7" mb={3}>
            If you’re currently on the Starter plan, please note that Analytics will no longer be
            included from 1 September. Analytics and access to Baotree’s AI features are included in
            our Pro and Enterprise plans.
          </Text>

          <Button
            size="sm"
            colorScheme="green"
            borderRadius="0"
            fontSize="xs"
            letterSpacing="0.06em"
            textTransform="uppercase"
            onClick={() => history.push(CHOOSE_PLAN_PATH)}
          >
            Choose your plan →
          </Button>

          <Text fontSize="sm" color="gray.700" lineHeight="1.7" mt={3} mb={0}>
            Not sure which option best suits your needs? Get in touch with our team:{' '}
            <Link href={`mailto:${SUPPORT_EMAIL}`} fontWeight="500" textDecoration="underline">
              {SUPPORT_EMAIL}
            </Link>
          </Text>
        </Box>

        {canDismiss && (
          <CloseButton
            position="absolute"
            right={{ base: 6, md: 16 }}
            top={4}
            size="sm"
            aria-label="Dismiss billing notice"
            onClick={handleDismiss}
          />
        )}
      </Alert>
    </Container>
  )
}

export default BillingTransitionBanner
